import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormLabel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../store';
import { Platform } from '../types/Platform';
import AppContext from '../context/AppContext';
import { setParams } from '../reducers/paramsReducer';
import {
  addSearchName,
  fetchSearchNames,
  removeSearchName,
} from '../reducers/searchNamesReducer';
import PlayerNameCombobox from './PlayerNameCombobox';

// The Player filter: the shared name combobox (PlayerNameCombobox) wired to the filter params and
// the signed-in user's saved names in Redux.
export default function GamesFiltersUser(): React.ReactNode {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useContext(AppContext);
  const { username, platform } = useAppSelector((state) => state.paramsReducer.params);
  const { names, loaded } = useAppSelector((state) => state.searchNamesReducer);

  const [usr, setUsr] = useState<string>();

  const placeholder = useMemo(
    () =>
      platform == Platform.ChessCom
        ? 'Chess.com handle'
        : platform == Platform.Lichess
          ? 'Lichess handle'
          : 'Chess.com / Lichess handle',
    [platform],
  );

  // Load the saved names once for a signed-in visitor (the endpoint 401s when signed out).
  useEffect(() => {
    if (isAuthenticated && !loaded) {
      dispatch(fetchSearchNames());
    }
  }, [isAuthenticated, loaded, dispatch]);

  useEffect(() => {
    setUsr(username ?? undefined);
  }, [username]);

  // Commit to the Player filter. Empty clears the player-relative filters too; a name is remembered for
  // the dropdown. No-op when unchanged, so a blur doesn't spam the server.
  const commit = (raw: string) => {
    const next = raw.trim();
    if (next == (username ?? '')) {
      return;
    }
    if (next == '') {
      dispatch(setParams({ username: null, color: null, result: null }));
    } else {
      dispatch(setParams({ username: next }));
      if (isAuthenticated) {
        dispatch(addSearchName(next));
      }
    }
  };

  // Clear the Player filter and the player-relative filters (color/result).
  const clear = () => {
    setUsr('');
    dispatch(setParams({ username: null, color: null, result: null }));
  };

  // Selected only once committed, not just typed.
  const hasSelected = username != null && username != '';

  return (
    <>
      <FormLabel>Player</FormLabel>
      <PlayerNameCombobox
        value={usr ?? ''}
        onChange={setUsr}
        onCommit={commit}
        onSelect={(name) => {
          setUsr(name);
          commit(name);
        }}
        onRemove={isAuthenticated ? (name) => dispatch(removeSearchName(name)) : undefined}
        names={names}
        committed={username}
        placeholder={placeholder}
        inputProps={{
          name: 'chess',
          style: hasSelected ? { paddingRight: '2rem' } : undefined,
        }}
      >
        {hasSelected ? (
          <button
            type="button"
            className="cm-player-filter-clear"
            title="Show all players"
            aria-label="Clear player filter"
            onClick={clear}
            style={{
              position: 'absolute',
              top: '50%',
              right: '0.5rem',
              transform: 'translateY(-50%)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1.4rem',
              height: '1.4rem',
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: 'inherit',
              opacity: 0.7,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        ) : null}
      </PlayerNameCombobox>
    </>
  );
}
